<template>
  <div class="container py-3">
    <div class="success-header text-center mb-5">
      <div class="success-icon mb-3">
        <i class="fa-solid fa-circle-check fa-beat-fade" 
           style="--fa-animation-duration: 2s; --fa-beat-fade-opacity: 0.2; --fa-beat-fade-scale: 1.2;"></i>
      </div>
      <h2 class="success-title">
        Presente enviado com sucesso!
      </h2>
      <p class="success-subtitle">Obrigado por tornar esse momento ainda mais especial 🎁</p>
    </div>

    <div class="identification-card mx-auto p-4">
      <h5 class="text-center mb-4">Deseja se identificar?</h5>
      <form @submit.prevent="enviarMensagem">
        <div class="mb-3">
          <label for="name" class="form-label">Seu nome</label>
          <input v-model="name" id="name" class="form-control" placeholder="Ex: João Silva" />
        </div>

        <div class="mb-4">
          <label for="message" class="form-label">Mensagem</label>
          <textarea v-model="message" id="message" rows="3" class="form-control" placeholder="Digite uma mensagem para os pais..."></textarea>
        </div>

        <div class="d-grid">
          <button class="btn btn-color-site" :disabled="loading">
            <span v-if="loading">
              <i class="fa fa-spinner fa-spin me-2"></i> Enviando...
            </span>
            <span v-else>
              Enviar mensagem
            </span>
          </button>
        </div>
      </form>

      <p class="text-muted mt-3 text-center small">* Essa etapa é opcional</p>
    </div>

    <div class="d-grid mt-4 mx-auto " style="max-width: 500px;">
      <button class="btn btn-outline-secondary" @click="goToEventDetails">
        Voltar para os detalhes do evento
      </button>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router'; // Importe useRouter
import api from '@/services/api'; 
import { toast } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';

export default {
  name: 'PaymentSuccess',
  setup() {
    const route = useRoute();
    const router = useRouter(); // Inicialize o router
    const eventId = route.params.eventId; // Pega o eventId da rota

    const name = ref('');
    const message = ref('');
    const loading = ref(false);

    const enviarMensagem = async () => {
      if (!name.value.trim() && !message.value.trim()) {
        toast.warning('Digite pelo menos seu nome ou uma mensagem.');
        return;
      }

      loading.value = true;

      try {
        await api.post(`/events/${eventId}/messages`, {
          name: name.value.trim(),
          message: message.value.trim()
        });

        toast.success('Mensagem enviada com sucesso!');
        name.value = '';
        message.value = '';
      } catch (err) {
        console.error("Erro ao enviar mensagem:", err);
        toast.error('Erro ao enviar mensagem. Por favor, tente novamente.');
      } finally {
        loading.value = false;
      }
    };

    // Nova função para redirecionar para a página do evento
    const goToEventDetails = () => {
      router.push(`/event/${eventId}`);
    };

    return {
      name,
      message,
      loading,
      enviarMensagem,
      goToEventDetails // Retorne a nova função para uso no template
    };
  }
};
</script>

<style scoped>
/* Container principal da tela de sucesso */
.success-screen-container {
  padding: 3rem 1rem;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #f8f9fa;
}

/* Estilos do cabeçalho de sucesso */
.success-header {
  color: #28a745;
  margin-bottom: 3rem !important;
}

.success-icon {
  font-size: 4.5rem;
  color: #28a745;
}

.success-title {
  font-size: 2.2rem;
  font-weight: 700;
  color: #343a40;
  margin-top: 1rem;
}

.success-subtitle {
  font-size: 1.1rem;
  color: #6c757d;
  margin-top: 0.5rem;
}

/* Estilização do card de identificação */
.identification-card {
  background-color: white;
  border-radius: 1rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1);
  padding: 2.5rem;
  max-width: 500px;
  width: 100%;
}

/* Estilo consistente para campos de input e textarea */
.form-control {
  outline: none !important;
  box-shadow: none !important;
  border: 1px solid #ccc;
  transition: border-color 0.2s ease-in-out;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
}
.form-control:focus {
  border-color: #888;
}

/* Propriedade específica para o textarea */
textarea {
  resize: vertical;
}

/* Estilos para o botão "Enviar mensagem" (consistente com o btn-color-site) */
.btn-color-site {
  background-color: #ff69b4;
  border-color: #ff69b4;
  color: white;
  font-weight: 600;
  padding: 0.75rem 1.25rem;
  border-radius: 0.5rem;
}
.btn-color-site:hover {
  background-color: #d65a98;
  border-color: #d65a98;
  color: white;
}
.btn-color-site:disabled {
  background-color: #ffb6d8;
  border-color: #ffb6d8;
}

/* Estilo para o novo botão de contorno (secondary) */
.btn-outline-secondary {
  border-color: #6c757d; /* Cor da borda */
  color: #6c757d; /* Cor do texto */
  font-weight: 600;
  padding: 0.75rem 1.25rem;
  border-radius: 0.5rem;
  transition: all 0.2s ease-in-out; /* Adiciona transição para hover */
}
.btn-outline-secondary:hover {
  background-color: #6c757d; /* Fundo cinza no hover */
  color: white; /* Texto branco no hover */
  border-color: #6c757d; /* Mantém a borda da mesma cor no hover */
}


/* Ajustes responsivos para telas menores */
@media (max-width: 768px) {
  .success-header {
    margin-bottom: 2rem !important;
  }
  .success-icon {
    font-size: 3.5rem;
  }
  .success-title {
    font-size: 1.8rem;
  }
  .success-subtitle {
    font-size: 1rem;
  }
  .identification-card {
    padding: 1.5rem;
  }
}
</style>